// Medya katmanının sözleşmesi (9.4.4.1). Uygulama platforma göre ayrışıyor —
// mobilde `react-native-webrtc` (native modül), web'de tarayıcının kendi
// `RTCPeerConnection`'ı — ama iki taraf da bu arayüzü karşılıyor. Tipler ayrı
// bir dosyada duruyor ki iki uygulama da birbirine bağlanmadan aynı şekli
// paylaşsın (`media.ts` ↔ `media.native.ts` döngüsü olmasın).
export type CallMedia = {
  /** Arayan tarafta: yerel teklifi üretir (non-trickle — 9.4.5.1). */
  createOffer(): Promise<string>;
  /** Aranan tarafta: gelen teklifi alır, cevabı üretir. */
  acceptOffer(offerSdp: string): Promise<string>;
  /** Arayan tarafta: karşıdan gelen cevabı bağlar. */
  setAnswer(answerSdp: string): Promise<void>;
  /**
   * Arayan tarafta: bağlantı koptuğunda ICE'yi yeniden başlatan yeni bir teklif
   * üretir (9.4.4.5). Mevcut oturum (mikrofon, track'ler) korunur; yalnız ağ
   * yolu tazelenir. Karşı tarafın cevabı `setAnswer` ile bağlanır.
   */
  restartIce(): Promise<string>;
  setMuted(muted: boolean): void;
  close(): void;
  /**
   * Medya bağlantısının durumunu bildirir (9.4.4.5). Sunucu ücreti duvar
   * saatinden hesapladığı için (`accepted_at` → `ended_at`) medyanın kopması
   * ücreti durdurmaz: kopmayı yalnız istemci görebilir ve çağrıyı bitirmezse
   * kullanıcı sessizlik için ödemeye devam eder.
   */
  onConnectionChange(cb: (state: MediaConnectionState) => void): void;
  /**
   * Görüntülü çağrıda akışları arayüze verir (9.4.14). Sesli çağrıda yerel akış
   * yalnız ses taşır ve uzak akış hiç çizilmez, o yüzden bu geri çağrı sesli
   * çağrıda da çalışır ama ekran bir şey göstermez.
   *
   * Uzak akış ANINDA gelmez: karşı taraf medyasını bağladıktan sonra `ontrack`
   * ile düşer. Bu yüzden tek seferlik bir okuma değil, değişimi bildiren bir
   * geri çağrı — ilk çizimde uzak taraf null olur ve sonra dolar.
   */
  onStreamChange(cb: (streams: CallStreams) => void): void;
  /**
   * Kamerayı kapatır/açar (9.4.14). Çağrı ve ses sürer; yalnız görüntü durur.
   * Track kaldırılmaz, `enabled` ile susturulur — kaldırmak yeniden pazarlık
   * (renegotiation) gerektirir ve karşı taraf için çağrının kopması gibi görünür.
   */
  setCameraEnabled(enabled: boolean): void;
  /** Ön ve arka kamera arasında geçiş (9.4.14). */
  switchCamera(): void;
};

/**
 * Akış nesnesi platforma göre farklı: native'de `react-native-webrtc`'nin
 * MediaStream'i, web'de tarayıcınınki. İkisinin çizim yolu da farklı olduğu için
 * tip burada bilinçli olarak opak; nasıl çizileceğini `CallVideo` bilir.
 */
export type CallStream = object;

export type CallStreams = { local: CallStream | null; remote: CallStream | null };

/** Medya kurulurken verilen seçenekler (9.4.14). */
export type CallMediaOptions = { video?: boolean };

/** WebRTC durumlarının bizi ilgilendiren sadeleştirmesi (9.4.4.5). */
export type MediaConnectionState = 'connecting' | 'connected' | 'interrupted' | 'failed';

/**
 * Kopukluk bu süre boyunca sürerse çağrı bitirilir (9.4.4.5). Amaç zararı
 * sınırlamak: bugün kopma hiç izlenmediği için çağrı, rezerve bakiye bitene
 * kadar sessiz sürüyor ve tamamı ücretlendiriliyor. Eşik kısa tutulmadı —
 * wifi↔hücresel geçişi birkaç saniye sürebilir ve çağrıyı boşuna düşürmek
 * de kötü deneyim.
 */
export const MEDIA_INTERRUPT_GRACE_MS = 30000;

/**
 * Kabulden sonra ilk medya yolu bu sürede hiç kurulamazsa çağrı kapatılır.
 * Bu, kurulmuş bir çağrının ağ geçişi toleransından ayrıdır: kullanıcı henüz
 * tek saniye ses alamadığı için daha kısa ve kesin bir sınır gerekir.
 */
export const MEDIA_INITIAL_CONNECT_TIMEOUT_MS = 15000;

/**
 * Kopukluk sürerken yapılacak ICE-restart denemelerinin BEKLEME aralıkları
 * (9.4.4.5). Kümülatif olarak 4., 10. ve 18. saniyelerde denenir ve
 * MEDIA_INTERRUPT_GRACE_MS (30 sn) dolmadan biter.
 *
 * Tek deneme yetmiyordu: gerçek bir wifi↔hücresel geçişte 4. saniyede cihaz
 * çoğu zaman yeni ağa daha bağlanmamış oluyor, deneme boşa gidiyor ve kalan
 * ~26 saniye boyunca kimse yeniden denemediği için kurtarılabilir çağrı
 * düşüyordu. Aralıklar artan: erken deneme ucuz, geç deneme ağ oturduktan
 * sonra yakalıyor.
 */
export const MEDIA_RECONNECT_DELAYS_MS = [4000, 6000, 8000];

export type IceServer = { urls: string; username?: string; credential?: string };

// Aday toplama tavanı. Non-trickle ICE (9.4.5.1) toplama bitene kadar bekler;
// bir aday kaynağı yanıt vermezse kurulum sonsuza kadar asılı kalmasın diye
// bu süre sonunda eldeki adaylarla devam edilir.
export const ICE_GATHER_TIMEOUT_MS = 5000;
